import {
  StackLayout,
  Button,
  HeadingText,
  ParagraphText,
  Box,
} from '@leagueplatform/genesis-core';
import { Image } from '@leagueplatform/genesis-commons';
import { useNavigateLegacy as useNavigate } from '@leagueplatform/routing';

import { FullScreenContainer } from 'components/full-screen-container/full-screen-container.component';
import pageIcon from 'assets/completion.svg';

interface ImageConfig {
  src: string;
  alt: string;
  width?: string;
  height?: string;
}

interface CompletionPageProps {
  onComplete?: () => void;
  title: string;
  description: string;
  continueButtonText?: string;
  dismissButtonText?: string;
  nextPath?: string;
  image?: ImageConfig;
}

const COMPLETION_PAGE_CONFIG = {
  defaultImage: {
    src: pageIcon,
    alt: 'completion image of two okay hands',
    width: '176px',
    height: 'auto',
  },
  styles: {
    container: {
      height: '100%',
      whiteSpace: 'pre-wrap',
      maxWidth: '351px',
      margin: '0 auto',
    },
    heading: {
      marginBottom: '$half',
    },
    description: {
      textAlign: 'center',
    },
    buttonContainer: {
      width: '180px',
      marginTop: '$twoAndHalf',
    },
    secondaryButton: {
      marginTop: '$half',
      color: '#48484A',
      '&:hover': {
        backgroundColor: 'transparent',
        color: 'inherit',
      },
    },
  },
};

export const CompletionPage = ({
  onComplete,
  title,
  description,
  continueButtonText,
  dismissButtonText,
  nextPath,
  image = COMPLETION_PAGE_CONFIG.defaultImage,
}: CompletionPageProps) => {
  const navigate = useNavigate();

  const onContinue = () => {
    onComplete?.();
    if (nextPath) {
      navigate({ pathname: nextPath });
    }
  };

  const onDismiss = () => {
    onComplete?.();
  };

  return (
    <FullScreenContainer>
      <StackLayout
        orientation="vertical"
        horizontalAlignment="center"
        verticalAlignment="center"
        css={COMPLETION_PAGE_CONFIG.styles.container}
      >
        <Image
          src={image.src}
          alt={image.alt}
          width={image.width ?? COMPLETION_PAGE_CONFIG.defaultImage.width}
          height={image.height ?? COMPLETION_PAGE_CONFIG.defaultImage.height}
          position="relative"
          top="minusOne"
          marginBottom="three"
        />
        <HeadingText
          level="3"
          size="xl"
          css={COMPLETION_PAGE_CONFIG.styles.heading}
        >
          {title}
        </HeadingText>
        {description && (
          <ParagraphText
            emphasis="subtle"
            css={COMPLETION_PAGE_CONFIG.styles.description}
          >
            {description}
          </ParagraphText>
        )}
        {(continueButtonText || dismissButtonText) && (
          <Box css={COMPLETION_PAGE_CONFIG.styles.buttonContainer}>
            {continueButtonText && (
              <Button width="fillContainer" onClick={onContinue}>
                {continueButtonText}
              </Button>
            )}
            {dismissButtonText && (
              <Button
                css={COMPLETION_PAGE_CONFIG.styles.secondaryButton}
                width="fillContainer"
                quiet
                size="medium"
                priority="tertiary"
                onClick={onDismiss}
              >
                {dismissButtonText}
              </Button>
            )}
          </Box>
        )}
      </StackLayout>
    </FullScreenContainer>
  );
};
